import { Routes, Route, Navigate } from 'react-router-dom';

import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider } from './context/AuthContext';
import { WorkspaceProvider } from './context/WorkspaceContext';
import { ChatProvider } from './context/ChatContext';
import { CallProvider } from './context/CallContext';
import { GroupCallProvider } from './context/GroupCallContext';
import { MeetingProvider } from './context/MeetingContext';
import { EmergencyProvider } from './context/EmergencyContext';

import { IncomingCallModal } from './components/calling/IncomingCallModal';
import { IncomingGroupCallModal } from './components/calling/IncomingGroupCallModal';
import { IncomingEmergencyModal } from './components/modals/IncomingEmergencyModal';
import { SendEmergencyModal } from './components/modals/SendEmergencyModal';
import { ActiveCallOverlay } from './components/calling/ActiveCallOverlay';
import { GroupCallGrid } from './components/calling/GroupCallGrid';
import { MeetingCallGrid } from './components/calling/MeetingCallGrid';

import { HomePage } from './pages/HomePage';
import { FeaturesPage } from './pages/FeaturesPage';
import { PricingPage } from './pages/PricingPage';
import { DownloadPage } from './pages/DownloadPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { AppPage } from './pages/AppPage';
import { PublicMeetingJoinPage } from './pages/PublicMeetingJoinPage';

import { SuperAdminRoute } from './components/auth/SuperAdminRoute';
import { PlatformLayout } from './pages/platform/PlatformLayout';
import { PlatformDashboardPage } from './pages/platform/PlatformDashboardPage';
import { PlatformWorkspacesPage } from './pages/platform/PlatformWorkspacesPage';
import { PlatformUsersPage } from './pages/platform/PlatformUsersPage';
import { PlatformWorkforceOverviewPage } from './pages/platform/PlatformWorkforceOverviewPage';

import { ErrorBoundary } from './components/common/ErrorBoundary';

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <WorkspaceProvider>
              <ChatProvider>
                <CallProvider>
                  <GroupCallProvider>
                    <MeetingProvider>
                      <EmergencyProvider>
                        {/* Incoming call / emergency / meeting overlays */}
                        <IncomingCallModal />
                        <IncomingGroupCallModal />
                        <IncomingEmergencyModal />
                        <SendEmergencyModal />
                        <ActiveCallOverlay />
                        <GroupCallGrid />
                        <MeetingCallGrid />

                        <Routes>
                          {/* =====================================================
                              PUBLIC WEBSITE ROUTES
                              ===================================================== */}
                          <Route path="/" element={<HomePage />} />
                          <Route path="/features" element={<FeaturesPage />} />
                          <Route path="/pricing" element={<PricingPage />} />
                          <Route path="/download" element={<DownloadPage />} />

                          {/* =====================================================
                              AUTH ROUTES
                              ===================================================== */}
                          <Route path="/login" element={<LoginPage />} />
                          <Route path="/register" element={<RegisterPage />} />

                          {/* =====================================================
                              INVITATION & MEETING JOIN ROUTES
                              ===================================================== */}
                          <Route
                            path="/invite"
                            element={<AcceptInvitePage />}
                          />

                          <Route
                            path="/meet/:token"
                            element={<PublicMeetingJoinPage />}
                          />

                          {/* =====================================================
                              AUTHENTICATED WORKSPACE APPLICATION
                              ===================================================== */}
                          <Route
                            path="/app/*"
                            element={<AppPage />}
                          />

                          <Route
                            path="/workspace/*"
                            element={<AppPage />}
                          />

                          {/* =====================================================
                              PLATFORM SUPER ADMIN ROUTES
                              ===================================================== */}
                          <Route
                            path="/platform"
                            element={<SuperAdminRoute />}
                          >
                            <Route element={<PlatformLayout />}>
                              <Route
                                index
                                element={
                                  <Navigate
                                    to="dashboard"
                                    replace
                                  />
                                }
                              />

                              <Route
                                path="dashboard"
                                element={<PlatformDashboardPage />}
                              />

                              <Route
                                path="workspaces"
                                element={<PlatformWorkspacesPage />}
                              />

                              <Route
                                path="users"
                                element={<PlatformUsersPage />}
                              />

                              <Route
                                path="workforce"
                                element={<PlatformWorkforceOverviewPage />}
                              />
                            </Route>
                          </Route>

                          {/* =====================================================
                              GLOBAL FALLBACK
                              ===================================================== */}
                          <Route
                            path="*"
                            element={<Navigate to="/" replace />}
                          />
                        </Routes>
                      </EmergencyProvider>
                    </MeetingProvider>
                  </GroupCallProvider>
                </CallProvider>
              </ChatProvider>
            </WorkspaceProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}